import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputBase from '@mui/material/InputBase';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import type { SelectProps } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import { styled } from '@mui/material/styles';
import { deriveHeroFrom, deriveHeroTo } from 'app/theme';
import type { ReactElement } from 'react';
import { HeroActionButton } from 'shared/components/HeroContainer';

export const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const FILTER_FIELD_BG_TRANSLUCENT = 'rgba(248, 246, 253, 0.6)';
const FILTER_FIELD_BORDER = 'rgba(34, 28, 90, 0.10)';
const FILTER_PLACEHOLDER = '#8782A0';
const CARD_BORDER = 'rgba(34, 28, 90, 0.07)';

export const FilterToolbarCard = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 16,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 14,
  backgroundColor: '#fff',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  padding: '12px 20px',
});

export const SearchFieldBox = styled(Box)({
  position: 'relative',
  flex: '1 1 220px',
  minWidth: 180,
});

export const SearchIconWrap = styled(Box)({
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  color: FILTER_PLACEHOLDER,
  pointerEvents: 'none',
});

export const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: 40,
  borderRadius: 10,
  border: `1px solid ${FILTER_FIELD_BORDER}`,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  paddingLeft: 36,
  paddingRight: 12,
  fontSize: 14,
  fontFamily: 'inherit',
  color: theme.palette.text.primary,
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: FILTER_PLACEHOLDER },
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}26`,
  },
}));

export const FilterSelect = styled(Select)(({ theme }) => ({
  minWidth: 160,
  height: 40,
  fontSize: 14,
  fontWeight: 500,
  flexShrink: 0,
  borderRadius: 10,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 14,
    paddingRight: '26px !important',
    paddingTop: 0,
    paddingBottom: 0,
    height: '40px !important',
  },
  '& .MuiSelect-icon': {
    width: 16,
    height: 16,
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#8782A0',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(34, 28, 90, 0.12)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main}80`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main}99`,
    borderWidth: 2,
  },
})) as unknown as <T = unknown>(props: SelectProps<T>) => ReactElement;

export const ActionCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

export const SearchAdornmentIcon = styled(SvgIcon)(({ theme }) => ({
  color: theme.palette.text.disabled,
  marginRight: theme.spacing(0.5),
  fontSize: '1.1rem',
}));

export const ClearFiltersLink = styled('button')(({ theme }) => ({
  flexShrink: 0,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: theme.palette.text.disabled,
  transition: 'color 0.15s ease',
  '&:hover': { color: theme.palette.text.primary },
}));

export const ApplyButton = styled(Button)(({ theme }) => ({
  height: 32,
  fontWeight: 600,
  fontSize: 12,
  borderRadius: 10,
  flexShrink: 0,
  paddingLeft: 12,
  paddingRight: 12,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  '&:hover': {
    background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${deriveHeroFrom(theme.palette.primary.main)} 50%, ${theme.palette.primary.main} 100%)`,
  },
  '&.Mui-disabled': {
    color: '#fff',
    background: theme.palette.action.disabledBackground,
  },
}));

export const AddInput = styled(InputBase)(({ theme }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  flexGrow: 1,
  fontSize: 14,
}));

/** Hero title */
export const HeroTitle = styled('h1')({
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.3,
});

/** Hero subtitle */
export const HeroSubtitle = styled('p')({
  margin: '4px 0 0',
  fontSize: 13,
  color: 'rgba(255,255,255,0.7)',
});

export const HeroAddButton = HeroActionButton;

export const DeleteMenuItem = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: 13,
}));

export const DeleteListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

export const FormFieldStack = styled(Stack)({
  paddingTop: 4,
});

export const FormDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

export const FormDialogContent = styled(DialogContent)({
  paddingTop: 0,
});

// ─── Form field shared styles ─────────────────────────────────────────────────

// ─── ClientFormModal styled components ───────────────────────────────────────

export const ModalDialog = styled(Dialog)({
  '& .MuiPaper-root': { borderRadius: '16px' },
});

export const ModalTitle = styled(DialogTitle)({
  fontSize: 16,
  fontWeight: 700,
  paddingBottom: 8,
});

export const ModalCancelButton = styled(Button)({
  fontSize: 13,
  textTransform: 'none',
  borderRadius: '8px',
});

export const ModalSubmitButton = styled(Button)({
  fontSize: 13,
  textTransform: 'none',
  borderRadius: '8px',
  fontWeight: 600,
});

// ─── ClientsPage hero banner layout ──────────────────────────────────────────

export const HeroBannerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: 0,
});

export const HeroBannerTextBox = styled(Box)({ minWidth: 0 });

export const HeroBannerButtonBox = styled(Box)({ marginLeft: 16 });

// ─── Filter status dropdown menu constants ────────────────────────────────────

export const STATUS_MENU_PAPER_SX = {
  mt: 0.5,
  minWidth: 130,
  borderRadius: '10px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  border: '1px solid rgba(0,0,0,0.07)',
  py: '4px',
};

export const STATUS_MENU_LIST_SX = { py: 0 };

export const STATUS_MENU_ITEM_SX = {
  fontSize: 13,
  fontWeight: 500,
  px: 1.5,
  py: '5px',
  minHeight: 0,
  textTransform: 'capitalize',
  borderRadius: '6px',
  mx: '4px',
  '&.Mui-selected': { backgroundColor: 'secondary.light', '&:hover': { backgroundColor: 'secondary.light' } },
};

export const FORM_FIELD_SX = {
  '& .MuiInputBase-input': { fontSize: 13 },
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiFormHelperText-root': { fontSize: 11 },
};

export const FORM_MENU_ITEM_SX = {
  fontSize: 13,
  fontWeight: 500,
  px: 1.5,
  py: '5px',
  minHeight: 0,
  borderRadius: '6px',
  mx: '4px',
  '&.Mui-selected': { backgroundColor: 'secondary.light' },
  '&.Mui-selected:hover': { backgroundColor: 'secondary.light' },
};

export const FORM_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: '10px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
      border: '1px solid rgba(0,0,0,0.07)',
      py: '4px',
    },
  },
  MenuListProps: { sx: { py: 0 } },
};

// ─── Row menu sizing (shared with tracker pattern) ────────────────────────────

export const CLIENTS_MENU_PAPER_SX = {
  minWidth: 148,
  borderRadius: '10px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  border: '1px solid rgba(0,0,0,0.07)',
  py: '4px',
};

export const CLIENTS_MENU_ITEM_SX = {
  px: 1.5,
  py: '5px',
  minHeight: 0,
  fontSize: 13,
  fontWeight: 500,
};

export const CLIENTS_MENU_TEXT_SX = { fontSize: 13, fontWeight: 500 };

// ─── ClientSelect styled components ──────────────────────────────────────────

const FORM_FIELD_BG = 'rgba(248, 246, 253, 0.6)';
const FORM_FIELD_BORDER = 'rgba(34, 28, 90, 0.10)';

export const ClientSelectTrigger = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: 40,
  padding: '0 12px',
  border: `1px solid ${FORM_FIELD_BORDER}`,
  borderRadius: 10,
  backgroundColor: FORM_FIELD_BG,
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.text.primary,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:hover': { borderColor: `${theme.palette.primary.main}40` },
  '&[data-open="true"]': {
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
    boxShadow: `0 0 0 4px ${theme.palette.secondary.main}1F`,
  },
}));

export const ClientSelectChevron = styled('span')<{ open: boolean }>(({ open }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  transition: 'transform 0.2s',
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  color: '#8782A0',
  fontSize: 16,
  marginLeft: 4,
}));

export const ClientSelectPopover = styled(Paper)(({ theme }) => ({
  width: 'var(--trigger-width)',
  borderRadius: 10,
  border: `1px solid rgba(0,0,0,0.07)`,
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  overflow: 'hidden',
  marginTop: 4,
  backgroundColor: theme.palette.background.paper,
}));

export const ClientSelectSearchBox = styled(InputBase)(({ theme }) => ({
  width: '100%',
  padding: '8px 14px',
  fontSize: 13,
  borderBottom: `1px solid rgba(0,0,0,0.07)`,
  '& input': { padding: 0 },
  '& input::placeholder': { color: theme.palette.text.disabled },
}));

export const ClientSelectList = styled('div')({
  maxHeight: 240,
  overflowY: 'auto',
  padding: '4px',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(47,39,117,0.2) transparent',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(47,39,117,0.2)', borderRadius: 4 },
});

export const ClientSelectItem = styled('div')<{ selected?: boolean }>(({ theme, selected }) => ({
  fontSize: 13,
  fontWeight: 500,
  padding: '6px 10px',
  borderRadius: 6,
  cursor: 'pointer',
  color: theme.palette.text.primary,
  backgroundColor: selected ? 'rgba(0,0,0,0.06)' : 'transparent',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
}));

export const ClientSelectEmpty = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.disabled,
  padding: '10px 14px',
  textAlign: 'center',
}));
